import React, { useState, useEffect } from "react";
import {
  X,
  Twitter,
  MessageCircle,
  Heart,
  UserPlus,
  Send,
  AlertCircle,
  DollarSign,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  useCreateTaskApiCallMutation,
  useUpdateTaskApiCallMutation,
} from "../../services/api/tasks/tasks.api";
import { toast } from "sonner";
import { Task } from "../../services/api/tasks/tasks.type";
import { Textarea } from "../ui/textarea";
import { useGetTokenPriceConversionApiCallQuery } from "../../services/api/campaigns/campaigns.api";

interface TaskBuilderProps {
  task?: Task | null;
  onSave?: (task: Omit<Task, "id">) => void;
  id: string;
  onCancel: () => void;
}

const TaskBuilder: React.FC<TaskBuilderProps> = ({
  task,
  onSave,
  onCancel,
  id,
}) => {
  console.log(task, "task");
  const [createTask] = useCreateTaskApiCallMutation();
  const [updateTask] = useUpdateTaskApiCallMutation();

  // use states here
  const [taskType, setTaskType] = useState("Like");
  const [platform, setPlatform] = useState("Twitter");
  const [instruction, setInstruction] = useState("");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [reward, setReward] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [isNewUser, setIsNewUser] = useState<boolean | null>(null);
  const [amount, setAmount] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const platformOptions = [
    {
      value: "Twitter" as const,
      label: "Twitter",
      icon: UserPlus,
      placeholder: "@username or profile URL",
      description: "Users must follow a Twitter account",
    },
    {
      value: "Telegram" as const,
      label: "Telegram",
      icon: Send,
      placeholder: "Tweet URL or ID",
      description: "Users must like a specific tweet",
    },
    {
      value: "BinGold" as const,
      label: "BinGold",
      icon: Heart,
      placeholder: "",
      description: "",
    },
  ];

  const taskTypeOptions = [
    {
      value: "Follow" as const,
      label: "Follow",
      icon: UserPlus,
      placeholder: "Twitter profile URL (e.g., https://x.com/BiGODToken)",
      description: "Users must follow a Twitter account",
      platforms: ["Twitter"],
    },
    {
      value: "Post" as const,
      label: "Post",
      icon: UserPlus,
      placeholder: "@username or profile URL",
      description: "Users must follow a profile",
      platforms: ["Twitter", "Facebook"],
    },
    {
      value: "Join" as const,
      label: "Join",
      icon: UserPlus,
      placeholder: "@username or profile URL",
      description: "Users must follow a profile",
      platforms: ["Telegram"],
    },
    {
      value: "Reshare" as const,
      label: "Reshare",
      icon: Twitter,
      placeholder: "Tweet URL or ID",
      description: "Users must retweet a specific tweet",
      platforms: ["Twitter", "Facebook"],
    },
    {
      value: "Like" as const,
      label: "Like",
      icon: Heart,
      placeholder: "Post URL or ID",
      description: "Users must like a post",
      platforms: ["Twitter", "Facebook"],
    },
    {
      value: "Comment" as const,
      label: "Comment",
      icon: Heart,
      placeholder: "Post URL or ID",
      description: "Users must like a post",
      platforms: ["Twitter", "Facebook"],
    },
    {
      value: "Create Account" as const,
      label: "Create Account",
      icon: MessageCircle,
      placeholder: "Sign up link or referral link",
      description:
        "Create a new account on the BinGold platform to earn rewards",
      platforms: ["BinGold"],
    },
    {
      value: "Buy" as const,
      label: "Buy",
      icon: MessageCircle,
      placeholder: "Minimum purchase amount",
      description: "Buy tokens on the BinGold platform to earn rewards",
      platforms: ["BinGold"],
    },
    {
      value: "Deposit" as const,
      label: "Deposit",
      icon: MessageCircle,
      placeholder: "Enter your registered BinGold email",
      description: "Users must deposit funds in the BinGold platform",
      platforms: ["BinGold"],
    },
    {
      value: "Login" as const,
      label: "Login",
      icon: UserPlus,
      placeholder: "",
      description: "Users must login to the BinGold platform at least once",
      platforms: ["BinGold"],
    },
    {
      value: "KYC" as const,
      label: "KYC",
      icon: UserPlus,
      placeholder: "",
      description: "Users must complete KYC verification on the BinGold platform",
      platforms: ["BinGold"],
    },
  ];

  const validateInstruction = (value: string): string => {
    if (!value.trim()) return "Instructions are required";
    if (value.length < 5) return "Instructions must be at least 5 characters";
    if (value.length > 500)
      return "Instructions must be less than 500 characters";
    return "";
  };

  const validateReward = (value: string): string => {
    if (!value) return "Reward amount is required";
    const num = Number(value);
    if (isNaN(num) || num <= 0) return "Reward must be a positive number";
    if (num > 1000000000000) return "Maximum 1 trillion tokens allowed";
    return "";
  };

  const validateAllFields = (): boolean => {
    const newErrors: Record<string, string> = {};

    newErrors.instruction = validateInstruction(instruction);
    newErrors.reward = validateReward(reward);

    setErrors(newErrors);

    const isValid = Object.values(newErrors).every((error) => !error);
    return isValid;
  };
  const validateField = (field: string, value: string) => {
    let error = "";

    if (field === "instruction") {
      error = validateInstruction(value);
    } else if (field === "reward") {
      error = validateReward(value);
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  // ✅ FIXED: Memoized filtered task types
  const filteredTaskTypes = React.useMemo(() => {
    return taskTypeOptions.filter((option) =>
      option.platforms.includes(platform),
    );
  }, [platform]);

  // ✅ FIXED: SPLIT useEffect into TWO separate effects
  // First: Set platform and other simple fields
  useEffect(() => {
    if (task) {
      setPlatform(task.platform);
      setInstruction(task.instructions);
      setReward(task.reward.toString());
      setUrl(task.url || "");
      setText(task.text || "");
      setIsNewUser(task.isNewUser ?? null);
      setAmount(task.amount ? task.amount : "");
    }
  }, [task]);

  // ✅ SECOND: Set taskType ONLY AFTER platform is set AND ONLY FOR TWITTER
  useEffect(() => {
    if (task && platform === task.platform) {
      // ✅ ONLY auto-select for Twitter platform
      // Validate taskType exists for Twitter
      const validTaskType =
        taskTypeOptions.find(
          (option) =>
            option.value === task.taskType &&
            option.platforms.includes(platform),
        )?.value || "Like";
      setTaskType(validTaskType);
    }
  }, [task, platform]); // ✅ Depends on platform - runs AFTER platform is set

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before saving");
      return;
    }
    if (!instruction.trim() || !reward.trim()) {
      toast.error("Please fill in all required fields");
      setIsLoader(false);
      return;
    }

    if (
      platform === "BinGold" &&
      (taskType === "Buy" || taskType === "Deposit") &&
      isNewUser === null
    ) {
      toast.error("Please select New or Existing User");
      setIsLoader(false);
      return;
    }

    if (taskType === "Buy" && (!amount || Number(amount) <= 0)) {
      toast.error("Please enter a valid buy amount");
      setIsLoader(false);
      return;
    }

    setIsLoader(true);

    try {
      const taskData = {
        platform,
        taskType: taskType,
        instructions: instruction.trim(),
        reward: parseFloat(reward),
        ...(!task && { campaignId: id }),
        url: url || undefined,
        taskId: task?._id,
        text: text || undefined,
        ...(platform === "BinGold" && taskType === "Create Account"
          ? { isNewUser: true }
          : {}),

        ...(platform === "BinGold" &&
        (taskType === "Buy" || taskType === "Deposit")
          ? { isNewUser }
          : {}),

        ...(taskType === "Buy" || taskType === "Deposit"
          ? { amount: Number(amount) }
          : {}),
      };

      const data = task
        ? await updateTask(taskData).unwrap()
        : await createTask(taskData).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        onCancel();
      }
    } catch (error) {
      toast.error("Failed to save task");
      console.error(error);
    } finally {
      setIsLoader(false);
    }
  };

  const getInstructionLabel = () => {
    const selectedTaskType = taskTypeOptions.find(
      (option) => option.value === taskType,
    );
    return selectedTaskType?.placeholder || "Profile";
  };

  const getInstructionHelp = () => {
    const selectedTaskType = taskTypeOptions.find(
      (option) => option.value === taskType,
    );
    return selectedTaskType?.description || "";
  };

  const selectedTaskType = taskTypeOptions.find(
    (option) => option.value === taskType,
  );

  const ErrorMessage = ({ field }: { field: string }) => {
    if (!errors[field]) return null;
    return (
      <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
        <AlertCircle className="h-4 w-4" />
        {errors[field]}
      </p>
    );
  };

  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-2xl my-auto max-h-[90vh] flex flex-col overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{task ? "Edit Task" : "Create New Task"}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* platform selection */}
            <div className="space-y-3">
              <Label htmlFor="taskType">Platform</Label>
              <Select
                value={platform}
                onValueChange={(value) => {
                  if (value === "BinGold") {
                    setTaskType("Create Account");
                  }
                  setPlatform(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {platformOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center space-x-2">
                          <Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              {/* {selectedTaskType && (
                <p className="text-sm text-gray-500">
                  {selectedTaskType.description}
                </p>
              )} */}
            </div>

            {/* Task Type Selection */}
            <div className="space-y-3">
              <Label htmlFor="taskType">Task Type</Label>

              <Select
                value={taskType}
                onValueChange={(value: Task["taskType"]) => setTaskType(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select task type" />
                </SelectTrigger>

                <SelectContent>
                  {filteredTaskTypes.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center space-x-2">
                          <Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {/* <Select
                value={taskType}
                onValueChange={(value: Task["type"]) => setTaskType(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {taskTypeOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center space-x-2">
                          <Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select> */}

              {/* {selectedTaskType && (
                <p className="text-sm text-gray-500">
                  {selectedTaskType.description}
                </p>
              )} */}
            </div>

            {/* Dynamic Instruction Field */}
            <div className="space-y-2">
              <Label htmlFor="instruction">Profile</Label>
              <Input
                id="instruction"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={"url"}
                // required
              />
              <p className="text-sm text-gray-500">{getInstructionHelp()}</p>
            </div>

            {taskType === "Post" && (
              <div className="space-y-2">
                <Label htmlFor="instruction">Text</Label>
                <Textarea
                  id="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={"Text"}
                  required
                />
                <p className="text-sm text-gray-500">{getInstructionHelp()}</p>
              </div>
            )}

            {platform === "BinGold" &&
              (taskType === "Buy" ||
                taskType === "Deposit" ||
                taskType === "Create Account") && (
                <div className="space-y-3">
                  <Label>User Type</Label>

                  <Select
                    value={isNewUser === null ? "" : isNewUser ? "new" : "old"}
                    onValueChange={(value) => setIsNewUser(value === "new")}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select user type" />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="new">New User</SelectItem>
                      <SelectItem value="old">Existing User</SelectItem>
                    </SelectContent>
                  </Select>

                  {!isNewUser && isNewUser !== null && (
                    <p className="text-sm text-gray-500">
                      Only existing users will be eligible for this task
                    </p>
                  )}
                </div>
              )}
            {(taskType === "Buy" || taskType === "Deposit") && (
              <div className="space-y-2">
                <Label htmlFor="amount">Minimum {taskType} Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter minimum  amount"
                  min="1"
                  required
                />

                <p className="text-sm text-gray-500">
                  User must buy at least this amount to complete the task
                </p>
              </div>
            )}

            {/* instruction  */}
            <div className="space-y-2">
              <Label htmlFor="reward">Instructions</Label>
              <Input
                id="instruction"
                type="text"
                value={instruction}
                onChange={(e) => {
                  setInstruction(e.target.value);
                  // ✅ FIXED: Real-time validation - ERROR DISAPPEARS IMMEDIATELY
                  validateField("instruction", e.target.value);
                }}
                placeholder="Enter instructions"
                required
              />
              <ErrorMessage field="instruction" />
              {/* <p className="text-sm text-gray-500">
                Number of tokens awarded for completing this task
              </p> */}
            </div>

            {/* Reward Amount */}
            <div className="space-y-2">
              <Label htmlFor="reward">Reward Amount (tokens)</Label>
              <Input
                id="reward"
                type="number"
                value={reward}
                onChange={(e) => {
                  setReward(e.target.value);
                  validateField("reward", e.target.value);
                }}
                placeholder="100"
                // min="0"
                required
              />
              <ErrorMessage field="reward" />
              {tokenPriceData?.result?.data && reward && (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      USDT Value:
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-900">
                      $
                      {getUSDTBalance(Number(reward)).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>
              )}
              <p className="text-sm text-gray-500">
                Number of tokens awarded for completing this task
              </p>
            </div>

            {/* Preview */}
            {/* {instruction && reward && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Task Preview</h4>
                <div className="space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Type:</span>{" "}
                    {selectedTaskType?.label}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Instruction:</span>{" "}
                    {instruction}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Reward:</span> {reward} tokens
                  </p>
                </div>
              </div>
            )} */}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 flex itcems-center gap-2"
                disabled={!instruction.trim() || !reward.trim()}
              >
                {task ? "Update Task" : "Save Task"}{" "}
                {isLoader && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskBuilder;
